<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import CalendarService from '@/core/shared/calendar/calendar.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { CalendarConnectionStatus } from '@/core/shared/calendar/types/calendar.types';

defineSlots<{
  trigger: () => void;
  disconnect: ({ handleDisconnectClick }: { handleDisconnectClick: () => void }) => void;
}>();

const calendarService = new CalendarService();

const isDisconnecting = ref(false);
const showDisconnectConfirmation = ref(false);

const isConnected = computed(() => {
  return calendarService.connectionStatus === CalendarConnectionStatus.ACTIVE;
});

const isConnectionFlowActive = computed(() => {
  return calendarService.connectionFlowActive;
});

async function prepareOAuthUrl() {
  try {
    const response = await calendarService.getInitiateOAuthLink();
    calendarService.calendarConnectionOauthUrl = response.authUrl;
  } catch (error) {
    ErrorService.captureException(error);
  }
}

async function handleConnectClick() {
  await calendarService.connectCalendar();
}

function cancelConnection() {
  calendarService.stopPolling();
}

const handleDisconnectClick = () => {
  showDisconnectConfirmation.value = true;
};

const confirmDisconnect = async () => {
  isDisconnecting.value = true;
  try {
    await calendarService.disconnectCalendar();
    showDisconnectConfirmation.value = false;
    await updateConnectionStatus();
  } catch (error) {
    ErrorService.captureException(error);
  } finally {
    isDisconnecting.value = false;
  }
};

const cancelDisconnect = () => {
  showDisconnectConfirmation.value = false;
};

const updateConnectionStatus = async () => {
  await calendarService.updateCurrentUserConnectionStatus();
  if (!isConnected.value || calendarService.currentUserNeedsUpgrade) {
    await prepareOAuthUrl();
  }
};

onMounted(updateConnectionStatus);

onUnmounted(() => {
  calendarService.stopPolling();
});
</script>

<template>
  <div class="inline-flex w-full items-center">
    <!-- Loading -->
    <div
      v-if="isConnectionFlowActive"
      class="flex w-full flex-col gap-2 rounded-lg bg-tint-40 p-4 text-sm"
    >
      <div class="pt flex gap-2">
        <v-progress-circular :size="20" :width="2" color="highlight-600" indeterminate />
        <div class="flex-1 font-bold">Connecting calendar...</div>
        <button
          @click="cancelConnection"
          type="button"
          class="hover:bg-gray-100 flex cursor-pointer items-center justify-center gap-2 rounded-xl text-sm font-bold text-shade-880"
        >
          Cancel
          <img src="@/assets/svg/x.svg" class="h-4 w-4" alt="Close" width="20" height="20" />
        </button>
      </div>
      <div class="text-sm font-normal text-shade-820">
        Complete your calendar authorization in the new tab to continue with scheduling.
      </div>
    </div>

    <!-- Connected state -->
    <div v-else-if="isConnected" class="flex w-full items-center gap-4">
      <div class="flex items-center gap-2 rounded-[20px] bg-primary-50 px-3 py-1 text-xs">
        <img src="@/assets/svg/check.svg" class="h-2 w-2" alt="Connected" />
        <span>Calendar connected</span>
      </div>
      <div v-if="$slots['disconnect']" class="flex flex-1 justify-end">
        <slot name="disconnect" :handleDisconnectClick="handleDisconnectClick" />
      </div>
    </div>

    <!-- Disconnected state with trigger slot -->
    <div v-else class="inline-flex">
      <a
        v-if="calendarService.calendarConnectionOauthUrl"
        :href="calendarService.calendarConnectionOauthUrl"
        target="_calendar_connection"
        @click="handleConnectClick"
        class="text-xs font-medium text-highlight-500 no-underline transition-colors hover:text-highlight-600 hover:underline"
      >
        <slot name="trigger">
          <span class="inline-flex items-center gap-1">Connect Calendar</span>
        </slot>
      </a>
      <v-progress-circular v-else :size="12" :width="2" color="highlight-600" indeterminate />
    </div>

    <v-dialog v-model="showDisconnectConfirmation" max-width="512px" persistent>
      <div class="rounded-lg bg-white p-6 text-base shadow-md">
        <div class="mb-2 font-serif text-2xl font-bold text-shade-900">Are you sure?</div>
        <div class="text-shade-900">Are you sure you want to disconnect your calendar?</div>

        <div class="mt-6 flex flex-row justify-end">
          <v-btn
            :ripple="false"
            class="modal-button-secondary"
            variant="flat"
            :loading="isDisconnecting"
            :disabled="isDisconnecting"
            @click.prevent="cancelDisconnect"
          >
            Cancel
          </v-btn>
          <v-btn
            :ripple="false"
            class="modal-button-primary ml-4"
            :loading="isDisconnecting"
            variant="flat"
            @click.prevent="confirmDisconnect"
          >
            Yes
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>
